import { useState } from 'react';
import { auth } from '../lib/api';
import { useAuth } from '../lib/auth';

interface Props {
  open: boolean;
  onCancel: () => void;
  onAccepted: () => void;
}

// One-time per user before they can download a mix file. Stronger
// commitment than the upload-time rights modal because the resulting file
// can be shared/redistributed by the user — we want a clear contractual
// "I won't" before handing them the bits.
export function DownloadAttestationModal({ open, onCancel, onAccepted }: Props) {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { refresh } = useAuth();

  if (!open) return null;

  async function handleContinue() {
    setSubmitting(true);
    try {
      await auth.ackDownload();
      await refresh();
      onAccepted();
    } catch (e) {
      // Server error is non-fatal — let the user retry by re-opening
      // the modal. Don't proceed without server-side ack.
      console.error('ackDownload failed', e);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="rl-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="dl-title">
      <div className="rl-modal">
        <h3 id="dl-title">Before you download</h3>
        <p>
          You're about to download a mix file derived from audio you uploaded. This file is
          for your personal practice only — please don't share or redistribute it. The
          underlying audio remains the property of its rights-holders.
        </p>
        <p>
          Mix downloads are only available for audio you uploaded directly. Sections recorded
          from YouTube can be practiced in the browser but cannot be exported, by design.
        </p>

        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '12px',
            background: 'var(--panel-2)',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
            marginBottom: 18,
          }}
        >
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            style={{
              marginTop: 2,
              width: 16,
              height: 16,
              accentColor: 'var(--accent-purple)',
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>
            I confirm this mix is for my personal practice only. I have the rights to
            process this audio and I won't share or redistribute the result.
          </span>
        </label>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} className="rl-btn-secondary" disabled={submitting}>
            Cancel
          </button>
          <button
            onClick={handleContinue}
            className="rl-btn-primary"
            disabled={!checked || submitting}
          >
            {submitting ? 'Saving…' : 'I agree, download'}
          </button>
        </div>
      </div>
    </div>
  );
}
